<!--
╔════════════════════════════════════════════════════════════════════╗
║  SectionRightImageHtml.vue - Immagine a Destra con HTML           ║
╚════════════════════════════════════════════════════════════════════╝

DESCRIZIONE:
Componente per "Opere d'Arte" con layout a due colonne: testo HTML +
eyebrow + titolo a sinistra, immagine/PDF a destra. Mirror di
SectionLeftImageHtml.

FEATURES:
  ✓ Layout grid 2 colonne (testo | immagine)
  ✓ Supporto immagini singole/carosello
  ✓ Visualizzatore PDF integrato
  ✓ Eyebrow con barra animata a sinistra
  ✓ Lightbox modal per zoom
  ✓ HTML content sanitizzato
  ✓ Responsive (collapse a 1 colonna su tablet)

LAYOUT:
  Desktop: [Titolo + Testo] [Immagine/PDF]
  Tablet:  [Titolo + Testo]
           [Immagine/PDF]

DIPENDENZE:
  - ImageCarousel.vue
  - PdfBlock.vue (involucro pigro di PdfViewer.vue)
  - sanitizeHtmlRegex
-->

<template>
  <section class="section-right-image-html">
    <div class="container">
      <div class="content">
        
        <div class="left">
          <div class="eyebrow">{{ content.eyebrow }}</div>
          <h2 class="title">{{ content.title }}</h2>

          <template v-if="!isTextExpanded">
            <div 
              ref="textTruncatedRef"
              class="html-content html-content-truncated" 
              v-html="sanitizeHtml(content.text)"
            ></div>
            <div v-if="isTextTruncated" class="expand-text-container">
              <button
                @click="isTextExpanded = true"
                class="expand-text-button"
              >
                Mostra altro
              </button>
            </div>
          </template>

          <template v-else>
            <div 
              class="html-content html-content-expanded" 
              v-html="sanitizeHtml(content.text)"
            ></div>
            <div class="expand-text-container">
              <button
                @click="isTextExpanded = false"
                class="expand-text-button"
              >
                Mostra meno
              </button>
            </div>
          </template>
        </div>

        <div class="right">
          
          <div class="pdf-container" v-if="content.pdf">
            <PdfBlock 
              :src="content.pdf" 
              :title="content.title"
            />
          </div>

          <div class="image-container" v-else-if="isSingleImage">
            <img 
              :src="content.image[0]" 
              :alt="content.title"
              class="content-image"
              @click="openLightbox"
            >
          </div>

          <div class="carousel-container-wrapper" v-else-if="isMultipleImages">
            <ImageCarousel 
              :images="content.image" 
              :title="content.title"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="isSingleImage && lightboxOpen" class="lightbox" @click="closeLightbox">
      <div class="lightbox-content">
        <img 
          :src="content.image[0]" 
          :alt="content.title"
          class="lightbox-image"
          @click.stop
        >
        <button class="lightbox-close" @click="closeLightbox">&times;</button>
      </div>
    </div>
  </section>
</template>

<script>
import ImageCarousel from './ImageCarousel.vue'
import PdfBlock from '@/components/sections/shared/PdfBlock.vue'
import { sanitizeHtmlRegex } from '@/utils/sanitizeHtml'

export default {
  name: 'SectionRightImageHtml',
  components: {
    ImageCarousel,
    PdfBlock
  },

  // ──────────────────────────────────────────────────────────────
  // PROPS DEFINITION
  // ──────────────────────────────────────────────────────────────
  props: {
    /**
     * Contenuto della sezione
     * @type {Object}
     * @required
     * @property {string} eyebrow - Testo eyebrow (sopra titolo)
     * @property {string} title - Titolo principale
     * @property {string} text - Testo HTML da sanitizzare
     * @property {string|Array} image - URL/Array di URL immagini
     * @property {string} pdf - URL PDF (prioritario su immagini)
     */
    content: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      /**
       * Stato lightbox
       * @type {boolean}
       */
      lightboxOpen: false,
      /**
       * Stato testo espanso (mobile)
       * @type {boolean}
       */
      isTextExpanded: false,
      /**
       * Flag se il testo è troncato
       * @type {boolean}
       */
      isTextTruncated: false
    }
  },

  // ──────────────────────────────────────────────────────────────
  // COMPUTED PROPERTIES
  // ──────────────────────────────────────────────────────────────
  computed: {
    /**
     * Verifica se content.image è un array con 1 elemento
     * @returns {boolean} true se è singola immagine
     */
    isSingleImage() {
      return Array.isArray(this.content.image) && this.content.image.length === 1
    },

    /**
     * Verifica se content.image è un array con più elementi
     * @returns {boolean} true se sono multiple immagini
     */
    isMultipleImages() {
      return Array.isArray(this.content.image) && this.content.image.length > 1
    }
  },

  // ──────────────────────────────────────────────────────────────
  // WATCHERS
  // ──────────────────────────────────────────────────────────────
  watch: {
    'content.text'() {
      this.isTextExpanded = false
      this.checkTextTruncation()
    }
  },

  // ──────────────────────────────────────────────────────────────
  // METHODS
  // ──────────────────────────────────────────────────────────────
  methods: {
    /**
     * Sanitizza HTML per evitare XSS
     * @param {string} html - HTML da sanitizzare
     * @returns {string} HTML sanitizzato
     */
    sanitizeHtml(html) {
      return sanitizeHtmlRegex(html || '')
    },

    /**
     * Controlla se il testo è troncato verificando se l'elemento ha altezza limitata da webkit-line-clamp
     */
    checkTextTruncation() {
      this.$nextTick(() => {
        const elem = this.$refs.textTruncatedRef
        if (elem && !this.isTextExpanded) {
          // Se il testo è in overflow, cioè scrollHeight > clientHeight, allora è troncato
          this.isTextTruncated = elem.scrollHeight > elem.clientHeight
        } else {
          this.isTextTruncated = false
        }
      })
    },

    /**
     * Apre lightbox e disabilita scroll
     */
    openLightbox() {
      this.lightboxOpen = true
      document.body.style.overflow = 'hidden'
    },

    /**
     * Chiude lightbox e ripristina scroll
     */
    closeLightbox() {
      this.lightboxOpen = false
      document.body.style.overflow = ''
    }
  },

  // ──────────────────────────────────────────────────────────────
  // LIFECYCLE HOOKS
  // ──────────────────────────────────────────────────────────────
  mounted() {
    this.checkTextTruncation()
  },

  beforeUnmount() {
    document.body.style.overflow = ''
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/main.scss';

/* ──────────────────────────────────────────────────────────────
   SECTION BASE STYLES - Layout a due colonne
   ────────────────────────────────────────────────────────────── */
.section-right-image-html {
  padding: $section-padding-y $container-padding-x $space-4 $container-padding-x;

  .container {
    max-width: $container-max-width;
    margin: 0 auto;
  }

  .content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: clamp(3rem, 6vw, 5rem);
    align-items: center;

    /* ──────────────────────────────────────────────────────────────
       LEFT COLUMN - Eyebrow, Titolo, Testo
       ────────────────────────────────────────────────────────────── */
    .left {
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding-right: $space-2;

      .eyebrow {
        position: relative;
        display: inline-block;
        font-size: $font-eyebrow;
        font-weight: 600;
        text-transform: uppercase;
        color: $clr-tertiary;
        margin-bottom: $space-2;
        cursor: default;
        white-space: nowrap; 
        transition: all 0.3s ease;

        &::before {
          content: '';
          position: absolute;
          bottom: 0;
          left: -20px;
          width: 10%;
          height: 12px;
          background-color: rgba($clr-tertiary, 0.7);
          transition: all 0.5s ease;
        }

        &:hover {
          color: $clr-tertiary;
          
          &::before {
            background: rgba($clr-tertiary, 0.8);
            width: calc(100% + 20px);
            transform: scaleY(1.2);
          }
        }
      }

      .title {
        font-size: $font-title;
        font-weight: 300;
        margin: 0;
        color: $text-color;
        margin-bottom: $space-2;
      }

      .html-content {
        font-size: $font-base;
        line-height: $line-height-base;
        color: $text-color;

        :deep(p) {
          margin: 0 0 $space-1 0;
          font-size: inherit;
          line-height: inherit;
          color: inherit;

          &:last-child {
            margin-bottom: 0;
          }
        }

        :deep(ul), :deep(ol) {
          margin: $list-spacing 0;
          padding-left: $list-indent;
        }

        :deep(li) {
          margin-bottom: $space-0-75;
        }

        :deep(strong) {
          font-weight: 700;
          color: $text-color;
        }

        :deep(em) {
          font-style: italic;
        }

        :deep(a) {
          color: $clr-tertiary;
          text-decoration: none;
        }

        &.html-content-truncated {
          @include line-clamp(4);
          transition: all 0.3s ease;
        }

        &.html-content-expanded {
          transition: all 0.3s ease;
        }
      }

      .expand-text-container {
        margin-top: clamp(1rem, 2vw, 1.5rem);
        display: flex;
        justify-content: center;
      }

      .expand-text-button {
        background: none;
        border: none;
        color: $clr-brand-dark;
        font-size: $font-list-meta;
        font-weight: 600;
        text-decoration: none;
        cursor: pointer;
        padding: clamp(0.75rem, 1.5vw, 1rem) clamp(1rem, 2vw, 1.5rem);
        border-radius: 4px;
        transition: all 0.3s ease;
        
        &:hover {
          color: $clr-tertiary;
          background-color: rgba($clr-tertiary, 0.1);
        }
        
        &:focus {
          outline: 2px solid $clr-tertiary;
          outline-offset: 2px;
        }
      }
    }

    /* ──────────────────────────────────────────────────────────────
       RIGHT COLUMN - Immagine/PDF/Carosello
       ────────────────────────────────────────────────────────────── */
    .right {
      display: flex;
      justify-content: center;
      align-items: center;

      .pdf-container {
        width: 100%;
        max-width: 750px;
        
        :deep(.pdf-viewer-container) {
          aspect-ratio: 750 / 842; // Via di mezzo singola/doppia pagina A4
          height: auto;
          min-height: 600px;
          max-height: 600px;
          border-radius: 0;
        }
      }

      .image-container {
        width: 100%;
        max-width: 750px;
        min-height: 400px;
        max-height: 400px;
        display: flex;
        align-items: center;
        justify-content: center;
        
        .content-image {
          width: 100%;
          height: 100%;
          max-height: 400px;
          object-fit: contain;
          cursor: zoom-in;
        }
      }

      .carousel-container-wrapper {
        width: 100%;
        max-width: 750px;
        min-height: 400px;
        max-height: 400px;
        display: flex;
        align-items: center;
        justify-content: center;
        
        :deep(.carousel-image) {
          max-height: 400px;
          height: 100%;
          object-fit: contain;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   LIGHTBOX STYLES - Modal overlay per zoom
   ────────────────────────────────────────────────────────────── */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $space-2;
  cursor: zoom-out;
  animation: fadeIn 0.3s ease;

  .lightbox-content {
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    align-items: center;
    justify-content: center;

    .lightbox-image {
      max-width: 100%;
      max-height: 90vh;
      object-fit: contain;
      cursor: default;
      animation: scaleIn 0.3s ease;
    }

    .lightbox-close {
      position: absolute;
      top: -40px;
      right: -40px;
      width: 40px;
      height: 40px;
      background: none;
      border: none;
      color: $clr-white;
      font-size: 3rem;
      cursor: pointer;
      transition: transform 0.2s ease;
      line-height: 1;
      padding: 0;

      &:hover {
        transform: scale(1.1);
      }
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Desktop medio (1300px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1300px) {
  .section-right-image-html {
    padding: $space-5 $container-padding-x-mob $space-4 $container-padding-x-mob;

    .content {
      gap: clamp(2.5rem, 5vw, 4rem);

      .left {
        padding-right: $space-1;

        .eyebrow {
          font-size: $font-eyebrow-lg;
          
          &::before {
            height: 10px;
          }
        }

        .title {
          font-size: $font-title-lg;
        }

        .html-content {
          font-size: $font-base-lg;
        }
      }

      .right {
        .pdf-container {
          max-width: 600px;
          
          :deep(.pdf-viewer-container) {
            aspect-ratio: 595 / 842;
            min-height: 700px;
          }
        }

        .image-container {
          max-width: 700px;
          min-height: 400px;
        }

        .carousel-container-wrapper {
          max-width: 700px;
          min-height: 400px;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Tablet (1024px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .section-right-image-html {
    .content {
      display: flex;
      flex-direction: column;
      @include flex-gap($space-1, column);

      .left {
        display: contents;

        .eyebrow {
          order: 1;
          margin-bottom: $space-0-5;
        }

        .title {
          order: 2;
          margin-bottom: 0;
        }

        .html-content {
          order: 4;
          margin-top: $space-1;
        }

        .expand-text-container {
          order: 5;
        }
      }

      .right {
        order: 3;
        width: 100%;
        margin-top: $space-1;
        
        .pdf-container {
          max-width: 100%;
          
          :deep(.pdf-viewer-container) {
            aspect-ratio: 595 / 842;
            min-height: 600px;
          }
        }

        .image-container {
          max-width: 480px;
          min-height: 400px;
        }

        .carousel-container-wrapper {
          max-width: 480px;
          min-height: 400px;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Mobile (768px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .section-right-image-html {
    padding: $space-1 $space-1 $space-3 $space-1;

    .content {
      gap: $space-1;

      .left {
        padding-right: 0;
        .eyebrow {
          font-size: $font-eyebrow-sm;
        }

        .title {
          font-size: $font-title-sm;
        }

        .html-content {
          font-size: $font-base-sm;

          /* Testo troncato solo su mobile */
          &.html-content-truncated {
            @include line-clamp(2);
          }
        }
      }

      .right {
        margin-top: $space-0-5;
        .pdf-container {
          max-width: 100%;
          
          :deep(.pdf-viewer-container) {
            aspect-ratio: 595 / 842;
            min-height: 500px;
          }
        }

        .image-container {
          max-width: 100%;
          min-height: 400px;
        }

        .carousel-container-wrapper {
          max-width: 100%;
          min-height: 400px;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Small Mobile (480px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .section-right-image-html {
    padding: 0 $container-padding-x-mob $section-padding-y-mobile $container-padding-x-mob;

    .content {
      gap: $space-1;

      .left {
        .eyebrow {
          /* .right diventa display:contents su mobile: senza align-self
             l'eyebrow eredita l'align-items:center di .content */
          align-self: flex-start;
          font-size: $font-eyebrow-xs;
          margin-left: 20px;
          margin-bottom: $space-0-25;
          white-space: normal;
        }

        .title {
          align-self: flex-start;
          font-size: $font-title-xs;
          margin-left: 20px;
          margin-bottom: $space-0-5;
        }

        .html-content {
          font-size: $font-base-xs;
        }

        .expand-text-container {
          margin-top: $space-0-25;
        }
      }

      .right {
        .pdf-container {
          margin-bottom: $space-0-5;
          :deep(.pdf-viewer-container) {
            aspect-ratio: 595 / 842;
            min-height: 550px;
          }
        }

        .image-container,
        .carousel-container-wrapper {
          min-height: auto;
          margin-bottom: $space-0-5;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Extra Small Mobile (360px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 360px) {
  .section-right-image-html {
    .content {
      .left {
        .eyebrow {
          font-size: $font-eyebrow-xxs;
          margin-left: 15px;
        }

        .title {
          font-size: $font-title-xxs;
          margin-left: 15px;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   ALLINEAMENTO EYEBROW / TITOLO — layout collassato
   Sotto i 1024px .left diventa display:contents: eyebrow e titolo
   sono figli flex diretti di .content (align-items: center) e, avendo
   larghezze diverse, venivano centrati ognuno per conto suo — l'eyebrow
   finiva a destra rispetto al titolo. align-self li riporta sulla stessa
   x, il margin-left la allinea a quella di "chi siamo".
   Valori misurati sul riferimento: x=54@600, 102@768/850, 122@1024,
   44@390, 39@360.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .section-right-image-html .content .left {
    .eyebrow,
    .title {
      align-self: stretch;
      text-align: left;
      margin-left: 50px;
    }
  }

  .section-right-image-html .content .html-content {
    align-self: stretch;
    margin-left: 50px;
  }
}

@media (max-width: 920px) {
  .section-right-image-html .content .left {
    .eyebrow,
    .title {
      align-self: stretch;
      text-align: left;
      margin-left: 30px;
    }
  }

  .section-right-image-html .content .html-content {
    align-self: stretch;
    margin-left: 30px;
  }
}

@media (max-width: 768px) {
  .section-right-image-html .content .left {
    .eyebrow,
    .title {
      align-self: stretch;
      text-align: left;
      margin-left: 22px;
    }
  }

  .section-right-image-html .content .html-content {
    align-self: stretch;
    margin-left: 22px;
  }
}

@media (max-width: 480px) {
  .section-right-image-html .content .left {
    .eyebrow,
    .title {
      align-self: stretch;
      text-align: left;
      margin-left: 20px;
    }
  }

  .section-right-image-html .content .html-content {
    align-self: stretch;
    margin-left: 20px;
  }
}

@media (max-width: 360px) {
  .section-right-image-html .content .left {
    .eyebrow,
    .title {
      align-self: stretch;
      text-align: left;
      margin-left: 15px;
    }
  }

  .section-right-image-html .content .html-content {
    align-self: stretch;
    margin-left: 15px;
  }
}
</style>
